/** The trade table on the Reports page.
 *
 * One row per trade, grouped by IST day and collapsed by default: what the
 * bot actually did — entry, exit, reason, P&L — beside the day's India VIX.
 *
 * It was a LIVE-vs-simulated comparison until 2026-08-16, with three extra
 * columns fed by a shadow engine replaying other exit styles on the same
 * prices. The operator removed that ("remove shadow completely, keep only
 * live"), so the simulated columns are gone and what remains is simply the
 * trade list. The `Comparison` names below are leftovers of that era and
 * are kept only because the API route and its response type still carry
 * them. */

import { useState, type ReactElement } from "react";
import { ContractLink } from "@/components/ui/ContractLink";
import type { TradeComparison, VixDay } from "@/lib/reportsApi";
import { istDayOf } from "./exitStats";
import { signedRupees, tone } from "./reportFormat";
import { RowModePill } from "./reportWidgets";

export function TradesMiniTable({
  trades,
  vix = [],
}: Readonly<{ trades: TradeComparison[]; vix?: VixDay[] }>): ReactElement {
  const vixByDay = new Map(vix.map((v) => [v.trade_date, v]));
  const days = groupByDay(trades);
  // Days start COLLAPSED (operator preference): the compact day-summary
  // view first, expand what you want to inspect.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const toggle = (day: string): void => {
    setExpanded((prev) => toggledSet(prev, day));
  };
  return (
    <div className="card mt-3 overflow-x-auto p-0">
      <ExpandCollapseBar
        onExpandAll={() => {
          setExpanded(new Set(days.map(([d]) => d)));
        }}
        onCollapseAll={() => {
          setExpanded(new Set());
        }}
      />
      <table className="w-full text-sm">
        <TradesHead />
        <tbody>
          {days.map(([day, dayTrades]) => (
            <DaySection
              key={day}
              day={day}
              trades={dayTrades}
              vix={vixByDay.get(day)}
              collapsed={!expanded.has(day)}
              onToggle={() => {
                toggle(day);
              }}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One column per exit style, live first then the three simulations. */
function TradesHead(): ReactElement {
  return (
    <thead className="text-[10px] uppercase tracking-wider text-ink-secondary">
      <tr className="border-b border-line">
        <th className="px-3 py-2 text-left">Time</th>
        <th className="px-3 py-2 text-left">Mode</th>
        <th className="px-3 py-2 text-left">Contract</th>
        <th className="px-3 py-2 text-right">Entry</th>
        <th className="px-3 py-2 text-right">LIVE</th>
        <th className="px-3 py-2 text-right">Fixed</th>
        <th className="px-3 py-2 text-right">Pivot</th>
        <th className="px-3 py-2 text-right">Swing</th>
        <th className="px-3 py-2 text-right">Swing 10</th>
      </tr>
    </thead>
  );
}

/** Trades grouped by the IST day their P&L landed (`report_at`), newest day
 * first, oldest trade first within the day — mirrors the reference
 * dashboard's day sections. A held basket sits under its CLOSE day; for an
 * intraday trade the two dates are the same. */
function groupByDay(trades: TradeComparison[]): [string, TradeComparison[]][] {
  const groups = new Map<string, TradeComparison[]>();
  for (const t of trades) {
    const day = istDayOf(t.report_at);
    const bucket = groups.get(day);
    if (bucket === undefined) groups.set(day, [t]);
    else bucket.push(t);
  }
  for (const bucket of groups.values()) {
    bucket.sort((a, b) => (a.report_at < b.report_at ? -1 : 1));
  }
  return [...groups.entries()].sort(([a], [b]) => (a > b ? -1 : 1));
}

function DaySection({
  day,
  trades,
  vix,
  collapsed,
  onToggle,
}: Readonly<{
  day: string;
  trades: TradeComparison[];
  vix: VixDay | undefined;
  collapsed: boolean;
  onToggle: () => void;
}>): ReactElement {
  const totals = dayTotals(trades);
  return (
    <>
      <tr className="border-b border-line bg-surface-2/60">
        <td colSpan={4} className="px-3 py-2">
          <button
            type="button"
            aria-label={collapsed ? `Expand ${day}` : `Collapse ${day}`}
            className="mr-2 text-ink-secondary hover:text-ink"
            onClick={onToggle}
          >
            <i className={`ti ${collapsed ? "ti-chevron-right" : "ti-chevron-down"} text-sm`} />
          </button>
          <span className="font-medium">{fullDayLabel(day)}</span>
          <span className="pill ml-2 bg-surface-2 text-ink-secondary">
            {String(trades.length)} trade(s)
          </span>
          {vix !== undefined && (
            <span className="ml-3 text-[11px] text-ink-muted">
              vix open: {vix.open.toFixed(2)} low: {vix.low.toFixed(2)} high: {vix.high.toFixed(2)}{" "}
              close: {vix.close.toFixed(2)}
            </span>
          )}
        </td>
        {totals.map((pnl, i) => (
          <DayTotalCell key={i} pnl={pnl} />
        ))}
      </tr>
      {!collapsed && trades.map((t) => <TradeRow key={t.trade_id} trade={t} />)}
    </>
  );
}

/** Column subtotals for one day, same order as `stylePnls`. */
function dayTotals(trades: TradeComparison[]): number[] {
  return trades.reduce<number[]>(
    (acc, t) => stylePnls(t).map((pnl, i) => (acc[i] ?? 0) + (pnl ?? 0)),
    [0, 0, 0, 0, 0],
  );
}

/** One P&L per column, in header order: LIVE then the simulations. */
function stylePnls(trade: TradeComparison): (number | null)[] {
  return [trade.live.pnl];
}

function TradeRow({ trade }: Readonly<{ trade: TradeComparison }>): ReactElement {
  return (
    <tr className="border-b border-line last:border-0">
      <td className="px-3 py-2">
        <TimeCell trade={trade} />
      </td>
      <td className="px-3 py-2">
        <RowModePill mode={trade.execution_mode} />
      </td>
      <td className="px-3 py-2">
        <InstrumentCell trade={trade} />
      </td>
      <td className="px-3 py-2 text-right">
        <EntryCell trade={trade} />
      </td>
      {stylePnls(trade).map((pnl, i) => (
        <PnlCell key={i} pnl={pnl} />
      ))}
    </tr>
  );
}

/** When. The row sits in its `report_at` day's section, so that is the time
 * shown — for an intraday trade that IS the entry. A basket is held across
 * days, so its close time would be stranded without saying what it closed
 * from, and the opening date rides underneath. */
function TimeCell({ trade }: Readonly<{ trade: TradeComparison }>): ReactElement {
  const held = istDayOf(trade.entry_at) !== istDayOf(trade.report_at);
  return (
    <span className="inline-flex flex-col">
      <span>{timeLabel(trade.report_at)}</span>
      {held && (
        <span className="text-[10px] text-ink-muted">from {shortDayLabel(trade.entry_at)}</span>
      )}
    </span>
  );
}

/** What was traded. A multi-leg basket has no single tradeable symbol — its
 * legs are separate contracts — so it gets a label rather than a dead link.
 * Before 2026-08-19 baskets never reached this table at all. */
function InstrumentCell({ trade }: Readonly<{ trade: TradeComparison }>): ReactElement {
  if (trade.horizon === "POSITIONAL") {
    return (
      <span className="inline-flex items-center gap-1 text-ink-secondary">
        <i className="ti ti-stack-2 text-xs" />
        Basket
      </span>
    );
  }
  return <ContractLink symbol={trade.contract_symbol} />;
}

/** What it cost to get in. One contract has a fill price per unit; a basket
 * has a NET cost for the whole structure (negative = opened for a credit), so
 * the two are not the same number and must not share a format. */
function EntryCell({ trade }: Readonly<{ trade: TradeComparison }>): ReactElement {
  if (trade.horizon === "POSITIONAL") {
    const net = trade.net_entry_cost;
    if (net === null || net === undefined) return <>—</>;
    return (
      <span title="Net cost of the whole basket (negative = opened for a credit)">
        {net < 0 ? `+${Math.abs(net).toFixed(0)} cr` : net.toFixed(0)}
      </span>
    );
  }
  return <>{trade.entry_fill_price !== null ? trade.entry_fill_price.toFixed(2) : "—"}</>;
}

/** Per-column day total on the day-header row — one under each of
 * LIVE / FIXED / PIVOT, bolder than the per-trade cells so the summary
 * row reads as a subtotal. */
function DayTotalCell({ pnl }: Readonly<{ pnl: number }>): ReactElement {
  return (
    <td className={`px-3 py-2 text-right font-semibold ${tone(pnl) ?? ""}`}>{signedRupees(pnl)}</td>
  );
}

function PnlCell({ pnl }: Readonly<{ pnl: number | null }>): ReactElement {
  if (pnl === null) return <td className="px-3 py-2 text-right text-ink-muted">—</td>;
  return (
    <td className={`px-3 py-2 text-right font-medium ${tone(pnl) ?? ""}`}>{signedRupees(pnl)}</td>
  );
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Kolkata",
  });
}

function shortDayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    timeZone: "Asia/Kolkata",
  });
}

function fullDayLabel(day: string): string {
  return new Date(`${day}T00:00:00+05:30`).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    weekday: "long",
    timeZone: "Asia/Kolkata",
  });
}

function ExpandCollapseBar({
  onExpandAll,
  onCollapseAll,
}: Readonly<{ onExpandAll: () => void; onCollapseAll: () => void }>): ReactElement {
  return (
    <div className="flex justify-end gap-2 border-b border-line px-3 py-2">
      <button type="button" className="btn btn-secondary px-2 py-1 text-xs" onClick={onExpandAll}>
        <i className="ti ti-chevrons-down text-xs" /> Expand all
      </button>
      <button type="button" className="btn btn-secondary px-2 py-1 text-xs" onClick={onCollapseAll}>
        <i className="ti ti-chevrons-up text-xs" /> Collapse all
      </button>
    </div>
  );
}

function toggledSet(prev: ReadonlySet<string>, day: string): ReadonlySet<string> {
  const next = new Set(prev);
  if (next.has(day)) next.delete(day);
  else next.add(day);
  return next;
}
